import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { Carrier } from '../types/common';
import type {
  CarrierAuthorizeUrl,
  CarrierClientCredentialsPayload,
  CarrierConnectionStatus,
  CarrierOrganization,
} from '../types/carriers';

export function useCarrierStatus(carrier: Carrier) {
  const api = useApiClient();
  return useQuery<CarrierConnectionStatus, Error>({
    queryKey: queryKeys.carriers.status(carrier),
    queryFn: ({ signal }) => api.get(`/admin/carriers/${carrier}/status`, signal),
  });
}

export function useConnectCarrier(carrier: Carrier) {
  const api = useApiClient();
  return useMutation<CarrierAuthorizeUrl, Error, void>({
    mutationFn: () => api.get(`/admin/carriers/${carrier}/connect`),
  });
}

export function useConnectCarrierClientCredentials(carrier: Carrier) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierConnectionStatus, Error, CarrierClientCredentialsPayload>({
    mutationFn: (payload) => api.post(`/admin/carriers/${carrier}/client-credentials`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.status(carrier) });
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.organizations(carrier) });
      // Connection state feeds the config-health banner via /admin/me.
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useCarrierCallback(carrier: Carrier) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierConnectionStatus, Error, { code: string; state: string }>({
    mutationFn: (payload) => api.post(`/admin/carriers/${carrier}/callback`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.status(carrier) });
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.organizations(carrier) });
      // Connection state feeds the config-health banner via /admin/me.
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useCarrierOrganizations(carrier: Carrier, enabled = true) {
  const api = useApiClient();
  return useQuery<CarrierOrganization[], Error>({
    queryKey: queryKeys.carriers.organizations(carrier),
    queryFn: async ({ signal }) => {
      const response = await api.get<{ data: CarrierOrganization[] }>(
        `/admin/carriers/${carrier}/organizations`,
        signal,
      );
      return response.data;
    },
    enabled,
  });
}

export function useSelectCarrierOrganization(carrier: Carrier) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierConnectionStatus, Error, { organization_id: string }>({
    mutationFn: (payload) => api.post(`/admin/carriers/${carrier}/select-organization`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.status(carrier) });
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useDisconnectCarrier(carrier: Carrier) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierConnectionStatus, Error, void>({
    mutationFn: () => api.del(`/admin/carriers/${carrier}/disconnect`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.status(carrier) });
      void qc.invalidateQueries({ queryKey: queryKeys.carriers.organizations(carrier) });
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}
